/**
 * Result Concept Modal Component
 * 
 * Displays detailed concept information including main concept image and element images.
 * Shows concept details like insights, benefits, and supporting claims.
 * Retrieves element images from 'elements_image_infos' API response.
 * 
 * @author Sabya
 * @date September 26, 2025
 * @modified Added element images display from elements_image_infos API
 * @modified Enhanced image fallback handling for both concept and element images
 */

import React from "react";
import Product1 from '../../assets/png/Large-product-1.png';
import Product2 from '../../assets/png/Large-product-2.png';
import Product3 from '../../assets/png/Large-product-3.png';
const conceptImages = [Product1, Product2, Product3];

const ConceptModal = ({ concept, isOpen, index }) => {
    if (!isOpen || !concept) return null;

    // Fixed: Extract image URL from nested API structure for modal display - September 23, 2025 - Sabya
    // Updated to handle concept.concept_image_infos[0].public_url and fallback paths
    const imageUrl = concept.concept_image_infos?.[0]?.public_url || 
                    concept.elements_image_infos?.[0]?.public_url || 
                    concept.image_url || 
                    concept.imageUrl || 
                    concept.stimulus_url || 
                    concept.stimulusUrl || 
                    conceptImages[index % conceptImages.length];

    return (
        <div
        >
            <div
                className="bg-white rounded-lg shadow-[0px_1px_24px_rgba(0,0,0,0.08)]  border border-[#E7EBF1] overflow-hidden"
                onClick={(e) => e.stopPropagation()}  // Prevent closing when clicking inside
            >

                <div className="flex flex-col">
                    <div className="py-4 px-8 border-b border-[#E7EBF1]">
                        <span className="font-poppins font-semibold text-base text-black">{concept.concept_name} - {concept.title}</span>
                    </div>

                    <div className="flex gap-2 p-3 py-5">
                        <img 
                            src={imageUrl} 
                            className="object-contain h-[250px] w-[250px] p-2" 
                            alt="product" 
                            height={250} 
                            width={250} 
                            loading="lazy"
                            onError={(e) => {
                                // Fallback to static images if API image fails to load
                                e.target.src = conceptImages[index % conceptImages.length];
                            }}
                        />

                        <div className="space-y-4 w-full">
                            <div className="flex flex-col gap-2">
                                <h3 className="text-sm font-semibold font-poppins">Insights</h3>
                                <p className="font-normal text-sm font-poppins">{concept.consumer_insight}</p>
                            </div>


                            <div className=' border-t border-[#E7EBF1] '></div>

                            <div className="flex flex-col gap-2">
                                <h3 className="text-sm font-semibold font-poppins">Benefits</h3>
                                <ul className="list-disc list-inside font-normal text-sm font-poppins">
                                    {/* {concept.benefits.map((benefit, index) => (
                                        <li key={index}>{benefit}</li>
                                    ))} */}
                                    <li>{concept.benefit_statement}</li>                                   
                                </ul>
                            </div>

                            <div className=' border-t border-[#E7EBF1] '></div>

                            <div className="flex flex-col gap-2">
                                <h3 className="text-sm font-semibold font-poppins">Supporting Claims & Reasons To Believe</h3>
                                <ul className="list-disc list-inside font-normal text-sm font-poppins">
                                    {/* {concept.supportingClaims.map((claim, index) => (
                                        <li key={index}>{claim}</li>
                                    ))} */}
                                    <li>{concept.supporting_claims_and_reasons_to_believe}</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Elements Images Section - Retrieved from elements_image_infos API */}
                    {concept.elements_image_infos && concept.elements_image_infos.length > 0 && (
                        <div className="grid grid-cols-3 w-full">
                            {concept.elements_image_infos.slice(0, 3).map((element, elementIndex) => (
                                <div
                                    key={elementIndex}
                                    className='p-4 text-center flex flex-col items-center justify-center border border-[#E7EBF1] min-h-[120px]'
                                >
                                    <img
                                        src={element.public_url || conceptImages[elementIndex % conceptImages.length]}
                                        alt={`Element ${elementIndex + 1}`}
                                        className="mx-auto mb-3 w-20 h-16 object-contain"
                                        onError={(e) => {
                                            // Fallback to static images if API image fails to load
                                            e.target.src = conceptImages[elementIndex % conceptImages.length];
                                        }}
                                    />
                                    <p className="text-sm text-gray-700">{element.description || `Element ${elementIndex + 1}`}</p>
                                </div>
                            ))}
                            {/* Fill remaining slots if less than 3 elements */}
                            {Array.from({ length: Math.max(0, 3 - (concept.elements_image_infos?.length || 0)) }, (_, index) => (
                                <div
                                    key={`empty-${index}`}
                                    className='p-4 text-center flex flex-col items-center justify-center border border-[#E7EBF1] min-h-[120px] bg-gray-50'
                                >
                                    <div className="w-20 h-16 bg-gray-200 rounded flex items-center justify-center mb-3">
                                        <span className="text-gray-400 text-xs">No Image</span>
                                    </div>
                                    <p className="text-sm text-gray-400">Element {(concept.elements_image_infos?.length || 0) + index + 1}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default ConceptModal;
